import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, RotateCcw, X } from "lucide-react";
import IconAction from "@/components/app/IconAction";
import api, { fmtIDR } from "@/lib_app/api";
import { toast } from "sonner";
import NumberInput from "@/components/app/NumberInput";

const MONTHS = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];

export default function MonthlyTargetModal({ open, onOpenChange, userRow, year, annualTarget, existingOverrides, onSaved }) {
  const [values, setValues] = useState(Array(12).fill(""));
  const [busy, setBusy] = useState(false);
  const monthlyAvg = (annualTarget || 0) / 12;

  useEffect(() => {
    if (open) {
      const arr = (existingOverrides || Array(12).fill(null)).map((v) => (v ? String(v) : ""));
      setValues(arr);
    }
  }, [open, existingOverrides]);

  const set = (i, v) => setValues((prev) => prev.map((x, idx) => (idx === i ? v : x)));

  const resetAll = () => setValues(Array(12).fill(""));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const overrides = values.map((v) => (v && Number(v) > 0 ? Number(v) : null));
      await api.put(`/targets/${userRow.user_id}/monthly`, { year, overrides });
      toast.success("Target bulanan disimpan");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const total = values.reduce((s, v, i) => s + (Number(v) || monthlyAvg), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="monthly-target-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#090808]">Target Bulanan · {userRow?.name}</DialogTitle>
          <DialogDescription>
            Kosongkan bulan yang mengikuti rata-rata ({fmtIDR(monthlyAvg)}). Isi hanya bulan yang mau di-override.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {MONTHS.map((m, i) => (
              <div key={i} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8]">{m}</Label>
                <NumberInput
                  value={values[i]}
                  onChange={(v) => set(i, v)}
                  data-testid={`month-override-${i}`}
                  placeholder={String(Math.round(monthlyAvg))}
                  className="font-num text-sm h-10"
                />
                {values[i] && (
                  <div className="text-[10px] text-[#A0A3A8] font-num">{fmtIDR(values[i])}</div>
                )}
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-[#F4F5F7] p-3 flex items-center justify-between text-sm">
            <span className="text-[#A0A3A8]">Total (bulanan digabung):</span>
            <span className="font-num font-bold text-[#090808]">{fmtIDR(total)}</span>
          </div>
          <DialogFooter className="gap-2 sm:justify-end">
            <IconAction icon={RotateCcw} label="Reset ke rata-rata" onClick={resetAll} testid="monthly-reset-btn" />
            <IconAction icon={X} label="Batal" onClick={() => onOpenChange(false)} testid="monthly-cancel-btn" />
            <IconAction icon={Save} label={busy ? "Menyimpan..." : "Simpan"} variant="primary" type="submit" disabled={busy} testid="monthly-save-btn" />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
